'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { Html5Qrcode as Html5QrcodeType } from 'html5-qrcode';

// Simplified translation function type — compatible with any useTranslations() return value
type TFunc = (key: string) => string;

interface UseQrCameraScannerOptions {
  containerId: string;
  stage: string;
  onScanValue?: (value: string) => void;
  t: TFunc;
}

interface UseQrCameraScannerResult {
  scannerReady: boolean;
  scannerError: string | null;
  scannedValue: string;
  clearScannedValue: () => void;
  startScanner: () => Promise<void>;
  stopScanner: () => Promise<void>;
}

export function useQrCameraScanner({
  containerId,
  stage,
  onScanValue,
  t
}: UseQrCameraScannerOptions): UseQrCameraScannerResult {
  const [scannerReady, setScannerReady] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [scannedValue, setScannedValue] = useState('');
  const scannerRef = useRef<Html5QrcodeType | null>(null);
  const restartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScannedRef = useRef<string | null>(null);

  const stopScanner = useCallback(async () => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) {
          // SCANNING state
          await scannerRef.current.stop();
        }
      } catch {
        // ignore stop errors
      }
      scannerRef.current = null;
    }
    setScannerReady(false);
  }, []);

  const startScanner = useCallback(async () => {
    // Clean up any existing scanner instance first
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) {
          await scannerRef.current.stop();
        }
      } catch {
        // ignore
      }
      scannerRef.current = null;
    }

    setScannerError(null);
    setScannedValue('');

    // Small delay to ensure the DOM element is rendered
    await new Promise((resolve) => setTimeout(resolve, 300));

    const container = document.getElementById(containerId);
    if (!container) return;

    // Clear leftover DOM from previous Html5Qrcode instance
    container.innerHTML = '';

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode(containerId);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1
        },
        (decodedText) => {
          // Extract container number — QR may contain "REG-2026-001-P1" format.
          // photo-scanning and qr-review need the full page QR (including -P{N}).
          const containerNumber =
            stage === 'photo-scanning' || stage === 'qr-review'
              ? decodedText
              : decodedText.replace(/-P\d+$/, '');
          setScannedValue(containerNumber);

          // Stop scanning after successful read
          scanner
            .stop()
            .then(() => {
              setScannerReady(false);
              scannerRef.current = null;
            })
            .catch(() => {});
        },
        () => {
          // QR not found in frame — ignore
        }
      );

      setScannerReady(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('cameraNotSupported');

      if (
        message.includes('NotAllowedError') ||
        message.includes('Permission')
      ) {
        setScannerError(t('cameraPermissionDenied'));
      } else if (
        message.includes('NotFoundError') ||
        message.includes('no camera')
      ) {
        setScannerError(t('cameraNotFound'));
      } else {
        setScannerError(t('cameraNotSupported'));
      }
    }
  }, [containerId, stage, t]);

  // Continuous scan mode: call onScanValue and restart scanner
  useEffect(() => {
    if (onScanValue && scannedValue) {
      // Skip duplicate consecutive scans (same QR still in frame)
      if (scannedValue === lastScannedRef.current) {
        setScannedValue('');
        if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
        restartTimerRef.current = setTimeout(() => startScanner(), 2000);
        return;
      }
      lastScannedRef.current = scannedValue;
      onScanValue(scannedValue);
      setScannedValue('');
      // Restart scanner after a brief delay
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      restartTimerRef.current = setTimeout(() => startScanner(), 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedValue, onScanValue]);

  const clearScannedValue = useCallback(() => {
    setScannedValue('');
    lastScannedRef.current = null;
  }, []);

  return {
    scannerReady,
    scannerError,
    scannedValue,
    clearScannedValue,
    startScanner,
    stopScanner
  };
}
